import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  resource,
  signal,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ExtraHoursData, ExtraHoursForm } from '@lib/extra-hours-form';
import { MilestoneForm, MilestoneFormData } from '@lib/milestone-form';
import { MilestoneTable } from '@lib/milestone-table';
import { ProjectForm, ProjectFormData } from '@lib/project-form';
import { PurchasedHoursTable } from '@lib/purchased-hours-table';
import { RemoveProjectData, RemoveProjectDialog } from '@lib/remove-project-dialog';
import { AgreementService, Auth, ProductService } from 'src/app/service';

import { Agreement, Milestone, Product, Project, Purchased } from '../schemas';
import { ProjectsService } from '../service/projects';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    MatInputModule,
    MatSortModule,
    MatTableModule,
    MatButtonModule,
    MatToolbarModule,
    MatTooltipModule,
    MilestoneTable,
    PurchasedHoursTable,
  ],
  selector: 'lcm-project-table',
  templateUrl: './project-table.html',
})
export class ProjectTable {
  projectService = inject(ProjectsService);

  dialog = inject(MatDialog);

  agreements = inject(AgreementService);

  products = inject(ProductService);

  auth = inject(Auth);
  owner = input.required<Agreement | Product>();

  updated = output();

  projectOwner = computed(() => (this.owner() as Agreement).AgreementNumber || this.owner().JobNumber);

  ownerType = computed(() => ((this.owner() as Agreement).AgreementNumber ? 'agreement' : 'opportunity'));

  milestones = computed<Milestone[]>(() => (this.owner() as Product).Milestones ?? []);

  purchasedHours = computed<Purchased[]>(() => this.owner().PurchasedHours ?? []);

  ownerCode = computed(() => this.ownerType() + ':' + this.projectOwner());

  pending = this.projectService.pending;

  projectsResource = resource({
    params: () => ({
      code: this.ownerCode(),
      milestones: this.milestones(),
      hours: this.purchasedHours(),
      projectOwner: this.projectOwner(),
      projects: this.projectService.projects(),
    }),
    defaultValue: [],
    loader: async ({ params }) => {
      const projects = params.projects.filter((it) => it.Owner == params.code);

      const collection = [
        // Fake "Main Project" for invoices not attached to specific projects

        Project.parse({
          Id: '',
          Owner: params.code,
          Title: `Invoices for Project ${params.projectOwner}`,
          Description: 'Invoices that are apart of the original project',
        }),
        ...projects,
      ];

      // Sort by ID then title for consistent ordering
      collection.sort((l, r) => l.Id.localeCompare(r.Id) || l.Title.localeCompare(r.Title));

      if (params.milestones.length) {
        this.combineMilestones(collection);
      }
      if (params.hours.length) {
        this.combinePurchasedHours(collection);
      }

      return collection as Project[];
    },
  });

  projects = computed(() => {
    const projects = this.projectsResource.value();

    projects.forEach((p) => {
      let type: string;

      if (!p.Id) {
        type = 'Main Project';
      } else if (p.PurchasedHours?.length) {
        type = 'Purchased Hours';
      } else if (p.Milestones?.length) {
        type = 'Milestones';
      } else {
        type = 'anything';
      }
      p['_Type'] = type;
    });
    return projects;
  });

  columnsToShow = signal(['Title', 'Description', '_Type', 'PurchaseOrder', 'StartDate', 'EndDate', 'actions']);
  sort = viewChild.required(MatSort);

  source = computed(() => {
    const source = new MatTableDataSource(this.projects());
    source.sort = this.sort();
    return source;
  });

  trackBy(idx: number, row: Project) {
    return row.Id;
  }

  protected items = [] as Project[];

  projectsForMilestones = computed(() => {
    const projects = this.projects();
    return projects.filter(({ Id, PurchasedHours }) => !Id || !PurchasedHours?.length);
  });
  projectsForPurchasedHours = computed(() => {
    const projects = this.projects();
    return projects.filter(({ Id, Milestones }) => !Id || !Milestones?.length);
  });

  expandedRows = signal({});
  expandRow(id: string) {
    this.expandedRows.update((expanded) => ({ ...expanded, [id]: !expanded[id] }));
  }

  reload(): void {
    this.projectService.reload();
  }

  combinePurchasedHours(items: Project[]) {
    const projects = items.reduce((projects, project) => {
      projects[project.Id!] = project;
      project.PurchasedHours = [];
      return projects;
    }, {});
    this.purchasedHours().forEach((item) => {
      // Find matching project by ID, or use empty string for main project
      const id = projects[item.ProjectId] ? item.ProjectId : '';
      if (!projects[id]) return;
      projects[id].PurchasedHours.push(item);
    });
  }

  combineMilestones(items: Project[]) {
    const projects = items.reduce((projects, project) => {
      projects[project.Id!] = project;
      project.Milestones = [];
      return projects;
    }, {});
    this.milestones().forEach((milestone) => {
      // sometimes project gets deleted and we put its invoices into fake main
      // project with empty ID then
      const id = projects[milestone.ProjectId] ? milestone.ProjectId : '';
      if (!projects[id]) return;
      projects[id].Milestones.push(milestone);
    });
  }

  remove(project: Project) {
    const data: RemoveProjectData = { project: project as Project };
    this.dialog
      .open(RemoveProjectDialog, { data })
      .afterClosed()
      .subscribe({
        next: (result) => result && this.reload(),
      });
  }

  edit(project?: Project) {
    const data: ProjectFormData = project ? { project, title: 'Edit project' } : { owner: this.ownerCode() };
    this.dialog
      .open(ProjectForm, { data })
      .afterClosed()
      .subscribe({
        next: (result) => result && this.reload(),
      });
  }

  addMilestone() {
    const data: MilestoneFormData = {
      title: 'Add milestone',
      ownerId: this.projectOwner(),
      projects: this.projectsForMilestones(),
    };
    this.dialog
      .open(MilestoneForm, { data })
      .afterClosed()
      .subscribe({
        next: (result) => {
          if (result) {
            this.reload();
            this.updated.emit();
          }
        },
      });
  }

  addPurchasedHours() {
    const service = this.ownerType() === 'agreement' ? this.agreements : this.products;

    const data: ExtraHoursData = {
      type: 'purchased',
      projects: this.projectsForPurchasedHours(),
      owner: this.projectOwner(),
      service,
    };

    this.dialog
      .open(ExtraHoursForm, { data })
      .afterClosed()
      .subscribe({
        next: (result) => {
          if (result) {
            this.reload();
            this.updated.emit();
          }
        },
      });
  }
}
